import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ScreenShell } from '../shell/ScreenShell';
import { VehicleSummaryHeader } from '../domain/VehicleSummaryHeader';
import { ChallanList } from '../domain/ChallanList';
import { StickyBottomBar } from '../domain/StickyBottomBar';
import { LoadingOverlay } from '../feedback/LoadingOverlay';
import { EmptyState } from '../feedback/EmptyState';
import { ErrorState } from '../feedback/ErrorState';
import { OfflineState } from '../feedback/OfflineState';
import { ChallanSkeleton } from '../feedback/ChallanSkeleton';
import { NudgeHeaderChip } from '../nudges/NudgeHeaderChip';
import { NudgeInlineCard } from '../nudges/NudgeInlineCard';
import { NudgeStickyBanner } from '../nudges/NudgeStickyBanner';
import { NudgeWhitespaceCard } from '../nudges/NudgeWhitespaceCard';
import { NudgePayNowAdjacency } from '../nudges/NudgePayNowAdjacency';
import { usePrototypeStore } from '../../stores/prototypeStore';
import { useSessionStore } from '../../stores/sessionStore';
import { useUiStore } from '../../stores/uiStore';
import { analytics } from '../../lib/analytics';
import { defaultVehicle } from '../../fixtures/vehicles';
import { challanFixtures } from '../../fixtures/challans';
import { fastagProfiles } from '../../fixtures/fastagProfiles';
import { computeRecommendedAmount } from '../../utils/qualification';
import type { FastagProfile } from '../../types';

export function ChallanListingScreen() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Prototype controls
  const activeVariant = usePrototypeStore((s) => s.activeVariant);
  const qualificationState = usePrototypeStore((s) => s.qualificationState);
  const balanceValue = usePrototypeStore((s) => s.balanceValue);
  const listLength = usePrototypeStore((s) => s.listLength);
  const simulateLoading = usePrototypeStore((s) => s.simulateLoading);
  const simulateEmpty = usePrototypeStore((s) => s.simulateEmpty);
  const simulateOffline = usePrototypeStore((s) => s.simulateOffline);
  const simulateError = usePrototypeStore((s) => s.simulateError);
  const deviceFrame = usePrototypeStore((s) => s.deviceFrame);
  const theme = usePrototypeStore((s) => s.theme);
  const writeToQuery = usePrototypeStore((s) => s.writeToQuery);

  // Session
  const selectedIds = useSessionStore((s) => s.selectedChallanIds);
  const dismissed = useSessionStore((s) => s.dismissedNudgeIds);
  const toggleChallan = useSessionStore((s) => s.toggleChallan);
  const dismissNudge = useSessionStore((s) => s.dismissNudge);
  const startRecharge = useSessionStore((s) => s.startRecharge);
  const startPayment = useSessionStore((s) => s.startPayment);

  // UI
  const setOffline = useUiStore((s) => s.setOffline);

  // Hydrate prototype controls from URL
  useEffect(() => {
    usePrototypeStore.getState().hydrateFromQuery(searchParams);
  }, []);

  // Sync URL when controls change
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    writeToQuery(params);
    setSearchParams(params, { replace: true });
  }, [
    activeVariant,
    qualificationState,
    balanceValue,
    listLength,
    deviceFrame,
    theme,
  ]);

  // Offline override
  useEffect(() => {
    setOffline(simulateOffline);
  }, [simulateOffline, setOffline]);

  const baseProfile: FastagProfile = fastagProfiles[qualificationState] ?? fastagProfiles.low;
  const profile: FastagProfile = {
    ...baseProfile,
    balance: balanceValue ?? baseProfile.balance,
    recommendedAmount: computeRecommendedAmount(balanceValue ?? baseProfile.balance),
  };

  const challans = simulateEmpty ? [] : challanFixtures[listLength];
  const pendingChallans = challans.filter((c) => c.status === 'pending');

  // Auto-select all pending on first load if nothing selected
  useEffect(() => {
    if (selectedIds.length === 0 && pendingChallans.length > 0) {
      useSessionStore.getState().selectAll(pendingChallans.map((c) => c.id));
    }
  }, []);

  const uiOffline = useUiStore((s) => s.isOffline);
  const selectedChallans = challans.filter((c) => selectedIds.includes(c.id));
  const total = selectedChallans.reduce((sum, c) => sum + c.amount, 0);
  const isOffline = simulateOffline || uiOffline;

  const isQualified = qualificationState !== 'unqualified';
  const isShortList = challans.length <= 2;

  // Variant visibility
  const showA = activeVariant === 'recommended' || activeVariant === 'A';
  const showB = activeVariant === 'recommended' || activeVariant === 'B';
  const showC = activeVariant === 'recommended' || activeVariant === 'C';
  const showD = activeVariant === 'recommended' || activeVariant === 'D';
  const showE = activeVariant === 'E';

  const bQualifies = isQualified && (qualificationState === 'low' || qualificationState === 'predicted-low');
  const cQualifies = isQualified && (qualificationState === 'negative' || (qualificationState === 'predicted-low' && profile.recentDecline));
  const dQualifies = isQualified && isShortList && (qualificationState === 'low' || qualificationState === 'predicted-low');

  const goToRecharge = (source: string) => {
    analytics.emit('recharge_started', {
      source,
      balance: profile.balance,
      recommended_amount: profile.recommendedAmount,
    });
    startRecharge(source);
    const params = new URLSearchParams({
      source,
      returnTo: `/challan-listing${window.location.search}`,
      balance: String(profile.balance),
      recommended_amount: String(profile.recommendedAmount),
    });
    navigate(`/fastag-recharge?${params.toString()}`);
  };

  const handlePay = () => {
    analytics.emit('pay_now_click', {
      variant: activeVariant,
      selected_count: selectedChallans.length,
      total,
    });
    startPayment();
    navigate('/payment-summary');
  };

  if (simulateLoading) {
    return (
      <ScreenShell title="Challans" showBack={false}>
        <LoadingOverlay />
        <VehicleSummaryHeader vehicle={defaultVehicle} />
        <div className="p-4">
          <ChallanSkeleton count={4} />
        </div>
      </ScreenShell>
    );
  }

  if (simulateError) {
    return (
      <ScreenShell title="Challans" showBack={false}>
        <ErrorState onRetry={() => usePrototypeStore.getState().setSimulateError(false)} />
      </ScreenShell>
    );
  }

  if (isOffline) {
    return (
      <ScreenShell title="Challans" showBack={false}>
        <OfflineState />
        <VehicleSummaryHeader vehicle={defaultVehicle} />
        <ChallanList
          challans={challans}
          selectedIds={selectedIds}
          onToggle={toggleChallan}
        />
      </ScreenShell>
    );
  }

  return (
    <ScreenShell
      title="Challans"
      showBack={false}
      headerSlot={
        <VehicleSummaryHeader
          vehicle={defaultVehicle}
          chipSlot={
            showA && isQualified ? (
              <NudgeHeaderChip profile={profile} visible={isQualified} />
            ) : null
          }
        />
      }
      bottomSlot={
        <>
          {showC && (
            <NudgeStickyBanner
              profile={profile}
              visible={cQualifies}
              dismissed={dismissed.includes('nudge_banner')}
              onRecharge={() => goToRecharge('sticky_banner')}
              onDismiss={() => {
                analytics.emit('nudge_dismiss', { nudge_id: 'nudge_banner' });
                dismissNudge('nudge_banner');
              }}
            />
          )}
          <StickyBottomBar
            total={total}
            count={selectedChallans.length}
            onPay={handlePay}
            disabled={selectedChallans.length === 0 || isOffline}
          />
        </>
      }
    >
      <div className="flex flex-col gap-3 pt-2">
        {showB && (
          <NudgeInlineCard
            profile={profile}
            visible={bQualifies}
            dismissed={dismissed.includes('nudge_inline')}
            onRecharge={() => goToRecharge('inline_card')}
            onDismiss={() => {
              analytics.emit('nudge_dismiss', { nudge_id: 'nudge_inline' });
              dismissNudge('nudge_inline');
            }}
          />
        )}
        {showE && (
          <NudgePayNowAdjacency
            profile={profile}
            visible={isQualified}
            onRecharge={() => goToRecharge('paynow_adjacency')}
          />
        )}

        {challans.length === 0 ? (
          <EmptyState />
        ) : (
          <ChallanList
            challans={challans}
            selectedIds={selectedIds}
            onToggle={(id) => {
              analytics.emit('challan_selection_toggle', { challan_id: id });
              toggleChallan(id);
            }}
          />
        )}

        {showD && (
          <NudgeWhitespaceCard
            profile={profile}
            visible={dQualifies}
            dismissed={dismissed.includes('nudge_whitespace')}
            onRecharge={() => goToRecharge('whitespace_card')}
            onDismiss={() => {
              analytics.emit('nudge_dismiss', { nudge_id: 'nudge_whitespace' });
              dismissNudge('nudge_whitespace');
            }}
          />
        )}
      </div>
    </ScreenShell>
  );
}
